// src/components/GoogleTranslate.jsx
import React, { useEffect, useRef } from "react";

const GoogleTranslate = ({
  supported = ["en", "nl", "fr", "de"],
  autoApply = true,
}) => {
  const langTitleMap = {
    en: "English",
    nl: "Dutch",
    fr: "French",
    de: "German",
  };
  const skipUntilRef = useRef(0);

  const getSubdomainLang = () => {
    const hostParts = window.location.hostname.split(".");
    if (
      hostParts[0] === "localhost" ||
      window.location.hostname.includes("127.0.0.1")
    )
      return "en";
    const sub = hostParts.length > 2 ? hostParts[0] : hostParts[0];
    return supported.includes(sub) ? sub : "en";
  };

  const setAppLanguage = (lang) => {
    if (!supported.includes(lang)) lang = "en";
    window.__APP_LANG = lang;
    window.dispatchEvent(
      new CustomEvent("app-lang-changed", { detail: { lang } })
    );
    console.info("[GTranslate] app language set:", lang);
  };

  // Simulate a real user interaction (pointerdown/mousedown/mouseup/click)
  const performUserLikeClick = (el) => {
    if (!el) return false;
    try {
      // focus first
      el.focus && el.focus();

      // create a sequence of pointer/mouse events
      const evInit = { bubbles: true, cancelable: true, view: window };
      ["pointerdown", "mousedown", "pointerup", "mouseup", "click"].forEach(
        (type) => {
          let ev;
          try {
            // pointer event if supported
            if (
              typeof PointerEvent === "function" &&
              type.startsWith("pointer")
            ) {
              ev = new PointerEvent(type, { ...evInit, pointerType: "mouse" });
            } else {
              ev = new MouseEvent(type, evInit);
            }
          } catch (e) {
            // fallback
            ev = document.createEvent("MouseEvents");
            ev.initMouseEvent(
              "click",
              true,
              true,
              window,
              1,
              0,
              0,
              0,
              0,
              false,
              false,
              false,
              false,
              0,
              null
            );
          }
          el.dispatchEvent(ev);
        }
      );

      // blur after a tick
      setTimeout(() => el.blur && el.blur(), 30);
      return true;
    } catch (err) {
      console.warn("[GTranslate] performUserLikeClick failed:", err);
      return false;
    }
  };

  // tolerant lang matcher (handles nl vs nl-NL etc.)
  const matchesLang = (value, target) => {
    if (!value || !target) return false;
    const v = (value + "").toLowerCase();
    const t = (target + "").toLowerCase();
    if (v === t) return true;
    if (v.startsWith(t)) return true;
    if (v.includes(t)) return true;
    return false;
  };

  useEffect(() => {
    const initialLang = getSubdomainLang();
    setAppLanguage(initialLang);

    window.gtranslateSettings = {
      default_language: initialLang,
      languages: supported,
      wrapper_selector: ".gtranslate_wrapper",
      detect_browser_language: false,
    };

    if (!document.getElementById("gtranslate-script")) {
      const s = document.createElement("script");
      s.id = "gtranslate-script";
      s.src = "https://cdn.gtranslate.net/widgets/latest/dwf.js";
      s.defer = true;
      s.onload = () => console.info("[GTranslate] dwf.js loaded");
      s.onerror = (e) => console.warn("[GTranslate] dwf.js failed to load", e);
      document.body.appendChild(s);
    }

    // Try to apply language to the widget (select or anchor style).
    const applyToWidget = () => {
      const target = initialLang;
      if (!autoApply) {
        console.debug(
          "[GTranslate] autoApply disabled — skipping widget application"
        );
        return false;
      }

      // SELECT variant: set selectedIndex and dispatch change/input events — plus user-like events
      const selectEl = document.querySelector(
        ".gt_switcher select, .goog-te-combo"
      );
      if (selectEl) {
        // find the best option index
        const opts = Array.from(selectEl.options || []);
        let found = -1;
        for (let i = 0; i < opts.length; i++) {
          const opt = opts[i];
          if (
            matchesLang(opt.value, target) ||
            matchesLang(opt.text, langTitleMap[target])
          ) {
            found = i;
            break;
          }
        }
        if (found >= 0) {
          try {
            selectEl.selectedIndex = found;
            // fire input + change + user-like events
            selectEl.dispatchEvent(new Event("input", { bubbles: true }));
            selectEl.dispatchEvent(new Event("change", { bubbles: true }));
            // also mouse events on the selected option element (if present)
            const optionEl = selectEl.options[found];
            optionEl && performUserLikeClick(optionEl);
            skipUntilRef.current = Date.now() + 2000;
            console.info("[GTranslate] applied language via select:", target);
            return true;
          } catch (e) {
            console.warn("[GTranslate] failed to apply via select:", e);
          }
        } else {
          console.debug("[GTranslate] select present but option not found yet");
        }
      }

      // ANCHOR variant: find the anchor and simulate an actual click sequence
      const anchors = Array.from(
        document.querySelectorAll(
          ".gt_option a, .gt_option .nturl, .gt_option a[data-gt-lang]"
        )
      );
      const anchor = anchors.find((a) => {
        const data = a.getAttribute && a.getAttribute("data-gt-lang");
        if (matchesLang(data, target)) return true;
        const title = (
          a.getAttribute("title") ||
          a.textContent ||
          ""
        ).toLowerCase();
        if (title.includes((langTitleMap[target] || "").toLowerCase()))
          return true;
        return false;
      });

      if (anchor) {
        // ensure dropdown open if required
        const selectedBtn = document.querySelector(
          ".gt_selected a, .gt_selected"
        );
        if (selectedBtn) performUserLikeClick(selectedBtn);
        // wait briefly then user-like click the anchor
        setTimeout(() => {
          const ok = performUserLikeClick(anchor);
          if (!ok) {
            try {
              anchor.click();
            } catch (e) {
              anchor.dispatchEvent(new MouseEvent("click", { bubbles: true }));
            }
          }
        }, 80);
        skipUntilRef.current = Date.now() + 2000;
        console.info(
          "[GTranslate] attempted to click anchor to set language:",
          target
        );
        return true;
      }

      console.debug("[GTranslate] widget anchors/options not available yet");
      return false;
    };

    // Poll briefly to attempt auto-apply (handles slow widget load)
    let attempts = 0;
    const pollMax = 12;
    const pollInterval = setInterval(() => {
      attempts++;
      const applied = applyToWidget();
      if (applied || attempts >= pollMax) clearInterval(pollInterval);
    }, 550);

    // Observe widget region and react when user manually changes; update app language state.
    const rootSelector = ".gt_switcher, .gtranslate_wrapper";
    const root = document.querySelector(rootSelector);
    const observer = new MutationObserver((mutations) => {
      if (Date.now() < skipUntilRef.current) return; // ignore programmatic window
      // prefer select check
      const select = document.querySelector(
        ".gt_switcher select, .goog-te-combo"
      );
      if (select) {
        const newLang = select.value;
        if (newLang && newLang !== window.__APP_LANG) {
          const short = newLang.split("-")[0];
          setAppLanguage(short);
        }
        return;
      }
      // anchor-based selected
      const sel = document.querySelector(".gt_selected a, .gt_selected");
      if (sel) {
        const dataLang = sel.getAttribute && sel.getAttribute("data-gt-lang");
        if (dataLang) {
          const short = dataLang.split("-")[0];
          if (short !== window.__APP_LANG) setAppLanguage(short);
          return;
        }
        const text = (
          sel.textContent ||
          sel.getAttribute("title") ||
          ""
        ).toLowerCase();
        const matchedKey = Object.keys(langTitleMap).find(
          (k) =>
            (langTitleMap[k] || "").toLowerCase().includes(text) ||
            text.includes((langTitleMap[k] || "").toLowerCase())
        );
        if (matchedKey && matchedKey !== window.__APP_LANG)
          setAppLanguage(matchedKey);
      }
    });

    // attach observer on body so we catch lazy-inserted widget
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      characterData: true,
    });

    // Also capture direct user clicks on widget anchors (fast detection)
    const clickHandler = (e) => {
      const a =
        e.target.closest && e.target.closest(".gt_option a[data-gt-lang]");
      if (a) {
        const data = a.getAttribute("data-gt-lang") || "";
        const short = data.split("-")[0].toLowerCase();
        if (short && short !== window.__APP_LANG) setAppLanguage(short);
      }
    };
    document.addEventListener("click", clickHandler, true);

    // cleanup
    return () => {
      clearInterval(pollInterval);
      observer.disconnect();
      document.removeEventListener("click", clickHandler, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, autoApply]);

  return <div className="gtranslate_wrapper" aria-hidden="true" />;
};

export default GoogleTranslate;
